/**
 * Dropdown customizado com tema escuro — evita o cinza do select nativo.
 * Lista aberta usa o mesmo fundo/borda do dashboard.
 */
import React, { useState, useRef, useEffect } from 'react';

const baseInputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '8px',
  border: '1px solid rgba(0, 212, 255, 0.3)',
  background: 'rgba(10, 14, 39, 0.8)',
  color: '#E2E8F0',
  fontSize: '14px',
  cursor: 'pointer',
  textAlign: 'left',
  position: 'relative',
  minHeight: '44px',
  boxSizing: 'border-box'
};

const dropdownListStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '4px',
  padding: '6px 0',
  borderRadius: '8px',
  border: '1px solid rgba(0, 212, 255, 0.4)',
  background: 'rgba(10, 14, 39, 0.98)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 212, 255, 0.15)',
  zIndex: 1000,
  maxHeight: '280px',
  overflowY: 'auto'
};

const optionStyle = {
  padding: '10px 14px',
  color: '#E2E8F0',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'background 0.15s ease'
};

const optionSelectedStyle = {
  ...optionStyle,
  background: 'linear-gradient(90deg, rgba(176, 38, 255, 0.25) 0%, rgba(0, 102, 255, 0.2) 100%)',
  color: '#FFFFFF'
};

const StyledSelect = ({ value, onChange, options = [], placeholder = '— Selecione —', disabled = false, style = {} }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedLabel = options.find(o => String(o.value) === String(value))?.label ?? placeholder;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          ...baseInputStyle,
          opacity: disabled ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          outline: 'none'
        }}
      >
        <span style={{ color: value ? '#E2E8F0' : '#6B7280' }}>{selectedLabel}</span>
        <span style={{ color: '#00D4FF', fontSize: '12px', marginLeft: '8px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={dropdownListStyle}>
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <div
                key={opt.value ?? 'empty'}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(opt)}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(0, 212, 255, 0.15)';
                  e.currentTarget.style.color = '#00D4FF';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isSelected ? 'linear-gradient(90deg, rgba(176, 38, 255, 0.25) 0%, rgba(0, 102, 255, 0.2) 100%)' : 'transparent';
                  e.currentTarget.style.color = isSelected ? '#FFFFFF' : '#E2E8F0';
                }}
                style={isSelected ? optionSelectedStyle : optionStyle}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StyledSelect;
